import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'

import { assetClient } from '../../clients/AssetClient'
import { GateFeedFilter, ObservationSort } from '../../clients/type'
import { userClient } from '../../clients/UserClient'
import { ReportType } from '../../models/client/constants'
import { GateObservation, GateFeedColumnKey, SortingAction } from '../../models/client/observation'
import { selectSiteIds } from '../../store/sites'
import { selectUserToken, selectUserUserId, selectUser } from '../../store/user'
import { ApiError } from '../../util/errors/ApiError'
import { getInitialDateFilter } from '../utils/reports'

const DEFAULT_COLUMNS: Array<GateFeedColumnKey> = [
  'assetPreviewUrl',
  'loadNumber',
  'loadDwellTimeHr',
  'hasSeal',
  'loadRemarks',
  'chassisNumber',
  'chassisDwellTimeHr',
  'vehicleNumber',
  'vehicleCarrierName',
  'assetDirection',
  'DATE',
  'TIME',
]

type Return = {
  assets: GateObservation[]
  filters: GateFeedFilter[]
  isLoading: boolean
  hasError: boolean
  page: number
  pageSize: number
  isColumnSettingsLoading: boolean
  totalPages: number
  totalItems: number
  itemIndex: number
  sort: ObservationSort | null
  userColumnKeys: Array<GateFeedColumnKey>
  setPage: (pageIndex: number) => void
  addFilter: (filter: GateFeedFilter) => void
  setSort: SortingAction
  removeFilter: (filter: GateFeedFilter) => void
  removePropertyFilters: () => void
  clearAllFilters: () => void
  setReportUserColumns: (nextColumnKeys: Array<GateFeedColumnKey>) => Promise<void>
  refresh: () => any
}

type Params = {
  reportType: ReportType
  queryPollMS?: number
  inventoryOnly?: boolean
  includeVehicles?: boolean
  defaultColumns?: Array<GateFeedColumnKey>
  defaultSiteId?: string
}

const PAGE_SIZE = 50

export const useReports = (params: Params): Return => {
  const queryClient = useQueryClient()

  const { startDate, endDate } = getInitialDateFilter()

  let initialSiteFilter = {}

  if (params.defaultSiteId) {
    initialSiteFilter = {
      type: 'site',
      id: 'site',
      label: 'Site',
      value: { label: 'Initial site', value: params.defaultSiteId },
    }
  }

  const [page, setPage] = useState(0)
  const [filters, setFilters] = useState<GateFeedFilter[]>([
    initialSiteFilter,
    {
      type: 'date',
      id: 'dateRange',
      label: 'Date range',
      value: {
        start: startDate,
        end: endDate,
      },
    },
  ])
  const [sort, setSort] = useState<ObservationSort | null>(null)

  const siteIds = useSelector(selectSiteIds, shallowEqual)
  const token = useSelector(selectUserToken, shallowEqual)
  const userId = useSelector(selectUserUserId, shallowEqual)
  const user = useSelector(selectUser, shallowEqual)

  const { reportType, defaultColumns = DEFAULT_COLUMNS, includeVehicles } = params

  const QUERY_KEY = useMemo(
    () => ['getSiteObservations', filters, token, page, reportType, sort],
    [filters, token, page, reportType, sort]
  )
  const COLUMN_KEY = ['getcolumnpreferences', userId, token, reportType]

  const {
    data,
    status,
    refetch,
    isFetching,
    isLoading: isQLoading,
  } = useQuery(
    QUERY_KEY,
    () => {
      if (!token) throw new Error('err')

      if (reportType === 'yardreport') {
        return assetClient.getYardReport({
          filters,
          token,
          page,
          size: PAGE_SIZE,
          includeVehicles,
          sort,
        })
      }
      return assetClient.getGateFeedReport({
        filters,
        token,
        page,
        size: PAGE_SIZE,
        includeVehicles,
        sort,
      })
    },
    {
      enabled: !!siteIds,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  )

  const {
    data: gatefeedMetadata,
    status: columnStatus,
    refetch: refetchColumns,
  } = useQuery(COLUMN_KEY, () => userClient.getGateFeedPreferences({ token, userId, reportType }), {
    enabled: !!token && !!userId,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  })

  const { mutateAsync: mutateColumns, status: mutateColumnStatus } = useMutation(
    userClient.setPreferencesGatefeedColumns,
    {
      onSuccess: (nextColumns) => {
        queryClient.setQueryData(COLUMN_KEY, () => ({
          columnKeys: nextColumns,
        }))
      },
    }
  )

  const isColumnSettingsLoading = mutateColumnStatus === 'loading' || columnStatus === 'loading'
  const isLoading = status === 'loading' || isFetching
  const total = data?.meta?.total || 0
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const itemIndex = page * PAGE_SIZE
  const { columnKeys } = gatefeedMetadata || {}

  return {
    filters,
    isColumnSettingsLoading,
    sort,
    assets: data?.data || [],
    page,
    pageSize: PAGE_SIZE,
    totalPages,
    totalItems: total,
    itemIndex,
    isLoading,
    hasError: status === 'error',
    userColumnKeys: columnKeys?.length ? columnKeys : defaultColumns,
    clearAllFilters() {
      setSort(null)
      setPage(0)
      setFilters([])
    },
    removeFilter(removedFilter) {
      setSort(null)
      setPage(0)

      setFilters((prev) => {
        const nextFilters: GateFeedFilter[] = []
        prev.forEach((filter) => {
          if (filter.id === removedFilter.id) return
          nextFilters.push(filter)
        })
        return nextFilters
      })
    },
    removePropertyFilters() {
      setSort(null)
      setPage(0)

      setFilters((prev) => {
        const nextFilters: GateFeedFilter[] = []
        prev.forEach((filter) => {
          if (filter.type === 'property') return
          nextFilters.push(filter)
        })
        return nextFilters
      })
    },
    addFilter(nextFilter) {
      const nextFilters: GateFeedFilter[] = [nextFilter]
      setSort(null)
      setPage(0)

      setFilters((prev) => {
        const existingFilters = prev.filter((filter) => {
          // Different filter types can coexist
          if (filter.type !== nextFilter.type) return true

          // For property filters, check if they have different property names
          if (filter.type === 'property') {
            const hasMatchingProperty = filter.value.some(
              (val, index) => val?.name && val.name === nextFilter.value[index]?.name
            )
            return !hasMatchingProperty
          }

          return false
        })

        return [...existingFilters, nextFilter]
      })
    },
    setPage(pageIndex) {
      setPage(pageIndex)
    },
    setSort(nextSort) {
      setSort(nextSort || null)
    },
    async setReportUserColumns(nextColumnKeys): Promise<void> {
      if (!token || !userId) throw new ApiError({ type: 'Unauthorized' })
      const meta = { ...user?.meta }
      meta.reports = { ...meta?.reports, [reportType]: nextColumnKeys }
      await mutateColumns({ token, userId, columnKeys: nextColumnKeys, reportType, meta })
      refetchColumns()
    },
    refresh() {
      return refetch()
    },
  }
}
